import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Label from "components/Label/Label";
import Input from "shared/Input/Input";
import Textarea from "shared/Textarea/Textarea";
import { selectEditingHolidayPackage, setEditingHolidayPackage } from "application/features/holidayPackage/holidayPackageSlice";
import { MoodEnum } from "data/enums";
import PriceSimulator from "components/Quasi/PriceSimulator/PriceSimulator";

const MOOD_OPTIONS = [
  { value: MoodEnum.AUTHENTIC, label: "Autentico" },
  { value: MoodEnum.BUSINESS, label: "Business" },
  { value: MoodEnum.COMFORT, label: "Comfort" },
  { value: MoodEnum.ENERGETIC, label: "Energico" },
];

const BasicInfoTab: React.FC<{ setIsDirty: (dirty: boolean) => void }> = ({ setIsDirty }) => {
  const dispatch = useDispatch();
  const editingHolidayPackage = useSelector(selectEditingHolidayPackage);

  const getTagsArray = (rawTags: unknown): string[] => {
    if (Array.isArray(rawTags)) {
      return rawTags
        .map((t) => String(t).trim())
        .filter((t) => t);
    }

    if (typeof rawTags === "string") {
      return rawTags
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t);
    }

    return [];
  };

  const handleInputChange = (field: string, value: any) => {
    dispatch(setEditingHolidayPackage({
      ...editingHolidayPackage,
      [field]: value
    }));
    setIsDirty(true);
  };

  const handleMoodSelect = (mood: string) => {
    dispatch(setEditingHolidayPackage({
      ...editingHolidayPackage,
      mood: editingHolidayPackage?.mood === mood ? undefined : mood
    }));
    setIsDirty(true);
  };

  const handleDurationChange = (value: string) => {
    const numValue = parseInt(value) || 1;
    dispatch(setEditingHolidayPackage({
      ...editingHolidayPackage,
      duration_days: Math.max(1, Math.min(30, numValue))
    }));
    setIsDirty(true);
  };

  const handleMinChange = (value: string) => {
    const numValue = parseInt(value) || 1;
    dispatch(setEditingHolidayPackage({
      ...editingHolidayPackage,
      min_guests: Math.max(1, numValue)
    }));
    setIsDirty(true);
  };

  const handleMaxChange = (value: string) => {
    const numValue = parseInt(value) || 1;
    dispatch(setEditingHolidayPackage({
      ...editingHolidayPackage,
      max_guests: numValue > editingHolidayPackage?.min_guests ? numValue : (editingHolidayPackage?.min_guests || 1)
    }));
    setIsDirty(true);
  }

  const handleLatestBookingChange = (value: string) => {
    dispatch(setEditingHolidayPackage({
      ...editingHolidayPackage,
      latest_booking: value
    }));
    setIsDirty(true);
  }

  const handleDefinedGuestsChange = (value: string) => {
    const numValue = parseInt(value) || 1;
    dispatch(setEditingHolidayPackage({
      ...editingHolidayPackage,
      defined_guests: Math.max(1, numValue)
    }));
    setIsDirty(true);
  }


  return (
    <div className="space-y-8">
      {/* Section Header */}
      <div>
        <h2 className="text-2xl font-semibold mb-2">Informazioni di base</h2>
        <p className="text-neutral-600 dark:text-neutral-400">
          Aggiorna le informazioni fondamentali del tuo pacchetto viaggio
        </p>
      </div>

      {/* Status Toggle Switch */}
      <div className="flex items-center gap-3 py-4 bg-gray-50 dark:bg-neutral-800 rounded-lg">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Stato pacchetto:
        </span>
        <button
          onClick={() => handleInputChange("stopped", !editingHolidayPackage?.stopped)}
          className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${editingHolidayPackage?.stopped
            ? 'bg-red-500'
            : 'bg-green-500'
            }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${editingHolidayPackage?.stopped ? 'translate-x-6' : 'translate-x-1'
              }`}
          />
        </button>
        <span className={`text-sm font-semibold ${editingHolidayPackage?.stopped
          ? 'text-red-600 dark:text-red-400'
          : 'text-green-600 dark:text-green-400'
          }`}>
          {editingHolidayPackage?.stopped ? 'In pausa' : 'Attivo'}
        </span>
      </div>

      {/* Name */}
      <div>
        <Label>Nome del pacchetto *</Label>
        <Input
          value={editingHolidayPackage?.name || ""}
          onChange={(e) => handleInputChange("name", e.target.value)}
          placeholder="Es: Weekend Romantico a Venezia"
          className="mt-2"
        />
      </div>

      {/* Description */}
      <div>
        <Label>Descrizione *</Label>
        <Textarea
          value={editingHolidayPackage?.description || ""}
          onChange={(e) => handleInputChange("description", e.target.value)}
          placeholder="Descrivi nel dettaglio cosa include il pacchetto viaggio..."
          rows={6}
          className="mt-2"
          maxLength={5000}
        />
        <p className="text-sm text-neutral-500 mt-2">
          {editingHolidayPackage?.description?.length || 0} / 5000 caratteri
        </p>
      </div>

      {/* Duration, People fields, Latest date - Two columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Duration */}
        <div>
          <Label>Durata (giorni) *</Label>
          <Input
            type="number"
            min="1"
            max="30"
            value={editingHolidayPackage?.duration_days || 1}
            onChange={(e) => handleDurationChange(e.target.value)}
            className="mt-2"
          />
          <p className="text-sm text-neutral-500 mt-2">
            Minimo 1 giorno, massimo 30 giorni
          </p>
        </div>

        {/* Latest date */}
        <div>
          <Label>Latest date *</Label>
          <Input
            type="number"
            min="0"
            value={editingHolidayPackage?.latest_booking || 0}
            onChange={(e) => handleLatestBookingChange(e.target.value)}
            className="mt-2"
          />
          <p className="text-sm text-neutral-500 mt-2">
            Giorni prima dell'inizio per prenotare
          </p>
        </div>

        {/* Persone - only for PREDEFINED package_type */}
        {editingHolidayPackage?.package_type === "defined_guests" && (
          <div>
            <Label>Numero persone *</Label>
            <Input
              type="number"
              min="1"
              value={editingHolidayPackage?.defined_guests || 1}
              onChange={(e) => handleDefinedGuestsChange(e.target.value)}
              className="mt-2"
            />
            <p className="text-sm text-neutral-500 mt-2">
              Minimo 1 persona
            </p>
          </div>
        )}

        {/* Min/Max persone - only for SHARING_GROUP package_type */}
        {editingHolidayPackage?.package_type === "sharing_group" && (
          <>
            {/* Min persone */}
            <div>
              <Label>Min. persone *</Label>
              <Input
                type="number"
                min="1"
                value={editingHolidayPackage?.min_guests || 1}
                onChange={(e) => handleMinChange(e.target.value)}
                className="mt-2"
              />
              <p className="text-sm text-neutral-500 mt-2">
                Minimo 1 persona
              </p>
            </div>

            {/* Max persone */}
            <div>
              <Label>Max. persone *</Label>
              <Input
                type="number"
                min="1"
                value={editingHolidayPackage?.max_guests || 1}
                onChange={(e) => handleMaxChange(e.target.value)}
                className="mt-2"
              />
              <p className="text-sm text-neutral-500 mt-2">
                Numero massimo di persone
              </p>
            </div>
          </>
        )}
      </div>

      {/* Price */}
      <div>
        <Label>
          {editingHolidayPackage?.package_type === "defined_guests"
            ? "Prezzo del pacchetto *"
            : "Prezzo a persona *"
          }
        </Label>
        <div className="flex gap-4 items-center justify-center max-w-2xl mt-2">
          <div className="flex-1">
            <Input
              type="number"
              min="0"
              step="1"
              value={editingHolidayPackage?.price || 0}
              onChange={(e) => {
                const numValue = parseFloat(e.target.value) || 0;
                dispatch(setEditingHolidayPackage({
                  ...editingHolidayPackage,
                  price: Math.max(0, numValue)
                }));
              }}
              placeholder="0.00"
            />
          </div>
          <PriceSimulator basePrice={editingHolidayPackage?.price || 0} />
        </div>
        <p className="text-sm text-neutral-500 mt-2">
          {editingHolidayPackage?.package_type === "defined_guests"
            ? "Il prezzo totale per l'intero pacchetto viaggio"
            : "Il prezzo per persona per il pacchetto viaggio"
          }
        </p>
      </div>

      {/* Mood Selection */}
      <div>
        <Label>Atmosfera del pacchetto</Label>
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1 mb-4">
          Seleziona l'atmosfera principale che meglio descrive il tuo pacchetto
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {MOOD_OPTIONS.map((mood) => (
            <button
              key={mood.value}
              onClick={() => handleMoodSelect(mood.value)}
              className={`p-4 rounded-xl border-2 transition-all text-center ${editingHolidayPackage?.mood === mood.value
                ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300"
                : "border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600"
                }`}
            >
              <div className="text-sm font-medium">{mood.label}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Tags */}
      <div>
        <Label>Tags</Label>
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1 mb-4">
          Inserisci i tags separati da virgola che descrivono meglio il tuo pacchetto
        </p>
        <Input
          value={editingHolidayPackage?.tags || ""}
          onChange={(e) => {
            const value = e.target.value.split(",").map(tag => tag.trim()).join(", ");
            handleInputChange("tags", value);
          }}
          placeholder="Es: spiaggia, relax, estate"
          className="mt-2"
        />
        <div className="my-4">
          {
            editingHolidayPackage?.tags && editingHolidayPackage?.tags.length > 0 ? (
              getTagsArray(editingHolidayPackage?.tags).map((tag, index) => (
                <span
                  key={tag || `tag-${index}`}
                  className="inline-block bg-neutral-200 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200 rounded-full px-3 py-1 mr-2 mb-2 text-sm"
                >
                  {tag}
                </span>
              ))
            ) : null
          }
        </div>
      </div>
    </div>
  );
};

export default BasicInfoTab;